import React from 'react'
import { match } from 'ts-pattern'

import type { IconProps } from '@slite/icons'
import {
  ApplyFormatIcon,
  AsanaIcon,
  AttioIcon,
  ConfluenceIcon,
  FileIcon,
  GitHubIcon,
  GitLabIcon,
  GlobeIcon,
  GoogleDriveIcon,
  HubspotIcon,
  InlineCodeIcon,
  IntercomIcon,
  JiraIcon,
  LinearIcon,
  NoteIconChart,
  NotionIcon,
  PenInBoxIcon,
  SearchAllIcon,
  SharepointIcon,
  SlackIcon,
  SliteAgentIcon,
  SparklesOutlineIcon,
  SpellcheckIcon,
} from '@slite/icons'
import type { SuperAssistantWorkflow } from '@slite/search-client'
import { SuperSource } from '@slite/search-client'

import { Favicon } from '../Favicon'

// TODO - import this from search client or source package once ready
enum SuperPlanTools {
  SEMANTIC_SEARCH = 'semanticSearch',
  GITHUB_SEARCH = 'githubSearch',
  GITHUB_AGGREGATION_SEARCH = 'githubAggregationSearch',
  LINEAR_SEARCH = 'linearSearch',
  LINEAR_AGGREGATION_SEARCH = 'linearAggregationSearch',
  ASANA_SEARCH = 'asanaSearch',
  ASANA_AGGREGATION_SEARCH = 'asanaAggregationSearch',
  JIRA_SEARCH = 'jiraSearch',
  JIRA_AGGREGATION_SEARCH = 'jiraAggregationSearch',
  INTERCOM_SEARCH = 'intercomSearch',
  INTERCOM_AGGREGATION_SEARCH = 'intercomAggregationSearch',
  ATTIO_SEARCH = 'attioSearch',
  ATTIO_AGGREGATION_SEARCH = 'attioAggregationSearch',
  HUBSPOT_SEARCH = 'hubspotSearch',
  HUBSPOT_AGGREGATION_SEARCH = 'hubspotAggregationSearch',
  // special
  WEB_SEARCH = 'webSearch',
  CHART_TOOL = 'chartTool',
  THINKING = 'thinking',
  FORMAT_RESPONSE = 'formatResponse',
  READ_CONTEXT = 'readContext',
  MCP = 'mcp',
  // final
  RESPOND_TO_QUESTION = 'respondToQuestion',
}

export function getSuperPlanStepIcon(
  stepTool: string,
  step?: SuperAssistantWorkflow['steps'][number]
) {
  return (
    match(stepTool)
      // SuperPlanTools
      .with(SuperPlanTools.SEMANTIC_SEARCH, () => SearchAllIcon)
      .with(SuperPlanTools.GITHUB_SEARCH, () => GitHubIcon)
      .with(SuperPlanTools.GITHUB_AGGREGATION_SEARCH, () => GitHubIcon)
      .with(SuperPlanTools.LINEAR_SEARCH, () => LinearIcon)
      .with(SuperPlanTools.LINEAR_AGGREGATION_SEARCH, () => LinearIcon)
      .with(SuperPlanTools.ASANA_SEARCH, () => AsanaIcon)
      .with(SuperPlanTools.ASANA_AGGREGATION_SEARCH, () => AsanaIcon)
      .with(SuperPlanTools.JIRA_SEARCH, () => JiraIcon)
      .with(SuperPlanTools.JIRA_AGGREGATION_SEARCH, () => JiraIcon)
      .with(SuperPlanTools.INTERCOM_SEARCH, () => IntercomIcon)
      .with(SuperPlanTools.INTERCOM_AGGREGATION_SEARCH, () => IntercomIcon)
      .with(SuperPlanTools.ATTIO_SEARCH, () => AttioIcon)
      .with(SuperPlanTools.ATTIO_AGGREGATION_SEARCH, () => AttioIcon)
      .with(SuperPlanTools.HUBSPOT_SEARCH, () => HubspotIcon)
      .with(SuperPlanTools.HUBSPOT_AGGREGATION_SEARCH, () => HubspotIcon)
      .with(SuperPlanTools.WEB_SEARCH, () => GlobeIcon)
      .with(SuperPlanTools.CHART_TOOL, () => NoteIconChart)
      .with(SuperPlanTools.RESPOND_TO_QUESTION, () => PenInBoxIcon)
      .with(SuperPlanTools.FORMAT_RESPONSE, () => ApplyFormatIcon)
      .with(SuperPlanTools.READ_CONTEXT, () => SpellcheckIcon)
      .with(SuperPlanTools.MCP, () => {
        const rootUrl = step?.parameters?.rootUrl
        if (typeof rootUrl !== 'string') {
          return InlineCodeIcon
        }
        return function McpIcon(props: IconProps) {
          return <Favicon {...props} url={rootUrl} size={16} />
        }
      })
      // SuperSource values
      .with(SuperSource.GITHUB, () => GitHubIcon)
      .with(SuperSource.GITLAB, () => GitLabIcon)
      .with(SuperSource.LINEAR, () => LinearIcon)
      .with(SuperSource.JIRA, () => JiraIcon)
      .with(SuperSource.ASANA, () => AsanaIcon)
      .with(SuperSource.INTERCOM, () => IntercomIcon)
      .with(SuperSource.ATTIO, () => AttioIcon)
      .with(SuperSource.HUBSPOT, () => HubspotIcon)
      .with(SuperSource.NOTION, () => NotionIcon)
      .with(SuperSource.SLACK_CHANNEL, () => SlackIcon)
      .with(SuperSource.CONFLUENCE, () => ConfluenceIcon)
      .with(SuperSource.GOOGLE_DRIVE, () => GoogleDriveIcon)
      .with(SuperSource.MICROSOFT_SHAREPOINT, () => SharepointIcon)
      .with(SuperSource.WEBSITE, () => GlobeIcon)
      .with(SuperSource.FILE, () => FileIcon)
      .with(SuperSource.CUSTOM, () => InlineCodeIcon)
      .with(SuperSource.SLITE, () => SearchAllIcon)
      .with(SuperSource.GIT, () => GitHubIcon)
      .with('file', () => FileIcon)
      .with('code', () => InlineCodeIcon)
      .with('read', () => SpellcheckIcon)
      .with('sparkles', () => SparklesOutlineIcon)
      .otherwise(() => SliteAgentIcon)
  )
}
